import React, { useContext, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { AddCircleOutline } from "@mui/icons-material"
import { Switch } from "@mui/material"
import Grid from "@mui/material/Grid"
import { Button, InputText, NotificationUtils, Select, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"

import { ClassificationSettingRequest } from "../../../../types/generated/api/ClassificationSettingRequest"
import type { ModelSettingsSetRequest } from "../../../../types/generated/api/ModelSettingsSetRequest"
import { convertResponseToTargets, deepJsonify } from "../../../../utils/modelDetailsHelpers"
import { Context } from "../../Context"
import type { ConfigurationsFormProps } from "../../Interfaces"
import { ConfigurationSlider } from "./ConfigurationSlider"

import styles from "./ConfigurationsForm.module.scss"

type ConfigurationFormikValues = {
  dataType: string
  target: string
  lowerLimit: number
  upperLimit: number
  step: number
  reDataType: string
  decimalNumCount: number
}

// Disabling rule since this will component will be refactored soon
// eslint-disable-next-line react-refresh/only-export-components
export let handleSubmit: () => ModelSettingsSetRequest

/**
 * Configuration Form
 * @return {React.ReactElement}
 */
export function ConfigurationsForm(props: Readonly<ConfigurationsFormProps>): React.ReactElement {
  const { responseJSON, modelId, errorMessage } = props

  const { isSidebarOpen, setIsSidebarOpen } = useContext(Context)

  const { values, errors, touched, setFieldValue, handleBlur } = useFormikContext<ConfigurationFormikValues>()
  const { t } = useTranslation("ModelConfigurations")

  const [sliders, setSliders] = useState<
    Array<{ label: { uuid: string | null; name: string | null }; range: [number, number] }>
  >([{ label: { uuid: null, name: null }, range: [0, 50] }])
  const [uncovered, setUncovered] = useState([[51, 100]])
  const [autoAdd, setAutoAdd] = useState<boolean>(true)
  const [showSnackBar, setShowSnackBar] = useState<boolean>(autoAdd)

  const requestValues = deepJsonify(responseJSON || {})

  const convertedTargets = convertResponseToTargets(responseJSON)
  const targets = convertedTargets.map((target) => target.join("."))

  const sliderIncrement = (value: number[], index: number): void => {
    const tempSliders = [...sliders]

    const secondValue = value[1]
    const tempCurrentSlider = tempSliders[index]
    const tempNextSlider = tempSliders[index + 1]
    // checks if:
    // 1. not the last in list
    // 2. value of the last tip of the slider is higher than the upper-limit
    // 3. value of the upper-limit slider is higher than lower-limit of the next slider
    if (
      secondValue &&
      tempNextSlider &&
      tempCurrentSlider &&
      secondValue >= tempNextSlider.range[0] &&
      tempCurrentSlider.range[1] >= tempNextSlider.range[0]
    ) {
      const decimalNumCount = values.decimalNumCount
      // Calculating new slider values
      for (let i = index + 1; i < sliders.length; i++) {
        const tempSlider = tempSliders[i]
        const tempPrevSlider = tempSliders[i - 1]
        // slider difference between its limits
        if (!tempSlider || !tempPrevSlider) continue
        const offset = tempSlider.range[1] - tempSlider.range[0]
        const upper = tempPrevSlider.range[1] + values.step
        const lower = tempPrevSlider.range[1] + offset + values.step

        // ensuring values are always within boundary range
        tempSlider.range = [
          upper >= values.lowerLimit ? +upper.toFixed(decimalNumCount) : values.lowerLimit,
          lower <= values.upperLimit ? +lower.toFixed(decimalNumCount) : values.upperLimit,
        ]
      }
    }

    setSliders(tempSliders)
  }

  const sliderDecrement = (value: number[], index: number): void => {
    const tempSliders = [...sliders]

    const firstValue = value[0]
    const tempCurrentSlider = tempSliders[index]
    const tempPrevSlider = tempSliders[index - 1]
    const tempNextSlider = tempSliders[index + 1]

    // checks if:
    // 1. not the first in list
    // 2. value of the first tip of the slider is lower than the lower limit
    // 3. value of the lower-limit slider is lower than upper-limit of the prev slider
    if (
      firstValue &&
      tempPrevSlider &&
      tempCurrentSlider &&
      tempNextSlider &&
      tempSliders[index - 1] &&
      firstValue <= tempPrevSlider.range[1] &&
      tempCurrentSlider.range[0] <= tempPrevSlider.range[1]
    ) {
      const decimalNumCount = values.decimalNumCount
      // Calculating new slider values
      for (let i = index - 1; i >= 0; i--) {
        // slider difference between its limits
        const offset = tempCurrentSlider.range[1] - tempCurrentSlider.range[0]
        const upper = tempNextSlider.range[0] - (offset + 1)
        const lower = tempNextSlider.range[0] - values.step

        tempCurrentSlider.range = [
          upper >= values.lowerLimit ? +upper.toFixed(decimalNumCount) : values.lowerLimit,
          lower <= values.upperLimit ? +lower.toFixed(decimalNumCount) : values.upperLimit,
        ]
      }
    }

    setSliders(tempSliders)
  }

  const handleRemoveLabel = (index: number): void => {
    const tempSliders = [...sliders]
    tempSliders.splice(index, 1)
    setSliders(tempSliders)
  }

  handleSubmit = (): ModelSettingsSetRequest => {
    const uniqueLabels = Array.from(new Set(sliders.map((slider) => slider.label.uuid)))

    // maps to the format needed by the backend
    const mappedLabels = sliders.map((slider) => ({
      type: ClassificationSettingRequest.type.LABEL,
      label: slider.label.uuid,
      lower_bound: slider.range[0],
      upper_bound: slider.range[1],
    }))

    if (uniqueLabels.length !== mappedLabels.length) {
      NotificationUtils.toast("Duplicate labels! please check that all labels are unique and try again", {
        severity: "error",
      })
      throw Error
    }

    const targetIndex = targets.indexOf(values.target)

    return {
      model: modelId,
      type: values.dataType as ModelSettingsSetRequest.type,
      target_key_path: convertedTargets[targetIndex] ?? [values.target],
      settings:
        values.dataType === t("dataTypes.range")
          ? [
              {
                type: ClassificationSettingRequest.type.BOUNDARY,
                lower_bound: values.lowerLimit,
                upper_bound: values.upperLimit,
                label: null,
              },
              ...mappedLabels,
            ]
          : undefined,
      auto_add: values.dataType === t("dataTypes.range") ? null : autoAdd,
    }
  }

  // updating uncovered sliders on any slider's change
  // TODO:: refactor to fire on change committed
  useEffect(() => {
    // Setting uncovered sliders
    const uncovered = []
    const tempSliders = [
      { range: [values.lowerLimit, values.lowerLimit] },
      ...sliders,
      { range: [values.upperLimit, values.upperLimit] },
    ]

    for (let i = 1; i < tempSliders.length; i++) {
      const tempPrevSlider = tempSliders[i - 1]
      const tempCurrentSlider = tempSliders[i]

      if (tempCurrentSlider && tempPrevSlider && tempCurrentSlider.range[0] - tempPrevSlider.range[1] > values.step)
        uncovered.push([
          i === 1
            ? +values.lowerLimit.toFixed(values.decimalNumCount)
            : +(tempPrevSlider.range[1] + values.step).toFixed(values.decimalNumCount),
          i === tempSliders.length - 1
            ? +values.upperLimit.toFixed(values.decimalNumCount)
            : +(tempCurrentSlider.range[0] - values.step).toFixed(values.decimalNumCount),
        ])
    }

    setUncovered(uncovered)
  }, [values.lowerLimit, values.step, values.upperLimit, sliders, values.decimalNumCount])

  return (
    <Grid container gap={2}>
      {errorMessage && (
        <Grid item xs={12}>
          <Snackbar severity="error" fullWidth description={errorMessage} />
        </Grid>
      )}

      {requestValues.length > 0 && targets.indexOf(values.target) > -1 && values.dataType !== values.reDataType && (
        <Grid item xs={12}>
          <Snackbar severity="error" fullWidth description={t("configForm.snackbars.mismatchType")} />
        </Grid>
      )}

      <Grid item xs={12} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}>
        {!isSidebarOpen && (
          <Button variant="secondary" onClick={setIsSidebarOpen.bind(null, (prev) => !prev)}>
            {isSidebarOpen ? t("forms.dryRun.close") : t("forms.dryRun.open")} {t("forms.dryRun.history")}
          </Button>
        )}
      </Grid>

      <Grid container item xs={12} gap={2} className={styles.containerBox}>
        <Typography variant="h3-bold">{t("configForm.titles.configureTarget")}</Typography>

        {(values.dataType === t("dataTypes.label") || values.dataType === "") && autoAdd && showSnackBar && (
          <Snackbar
            fullWidth
            removable
            severity="info"
            title={t("configForm.snackbars.autoAdd.title")}
            description={t("configForm.snackbars.autoAdd.description")}
            onRemove={() => setShowSnackBar(false)}
          />
        )}

        <Grid
          container
          item
          xs={12}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"end"}
          flexWrap="nowrap"
          gap={2}
        >
          <Select
            value={values.dataType === "" ? t("dataTypes.label") : values.dataType}
            label={t("configForm.fields.targetType")}
            optionsWithValues={[
              {
                label: t("configForm.fields.options.label"),
                value: t("dataTypes.label"),
              },
              {
                label: t("configForm.fields.options.range"),
                value: t("dataTypes.range"),
              },
            ]}
            handleChange={(e) => {
              setFieldValue("dataType", e.target.value)
            }}
            fullWidth
          />
          <InputText
            id="target"
            label={t("configForm.fields.target.label")}
            placeholder={t("configForm.fields.target.placeholder")}
            value={values.target}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            handleChange={(e: any) => {
              let dataType: string

              const targetKey = targets.indexOf(e.target.value)

              if (targets.length !== 0 && requestValues[targetKey] !== undefined) {
                if (typeof requestValues[targetKey] === "number") {
                  dataType = t("dataTypes.range")
                } else {
                  dataType = t("dataTypes.label")
                }

                setFieldValue("dataType", dataType)
                setFieldValue("reDataType", dataType)
              }

              setFieldValue("target", e.target.value)
            }}
            handleBlur={handleBlur}
            error={touched.target && Boolean(errors.target) && errors.target}
            fullWidth
            required
            hideDescription
            options={responseJSON ? targets : undefined}
          />
        </Grid>

        <Grid item xs={12} display={"flex"} gap={1} justifyContent="space-between">
          {(values.dataType === t("dataTypes.label") || values.dataType === "") && (
            <Grid item xs={12}>
              <Grid item>
                <Typography variant="label-bold" gutterBottom tooltip={t("configForm.snackbars.autoAdd.description")}>
                  {t("configForm.snackbars.autoAdd.title")}
                </Typography>
              </Grid>

              <Grid container item spacing={1} alignItems={"center"}>
                <Switch checked={autoAdd} onChange={() => setAutoAdd((prev) => !prev)} />
                <Typography variant="p" textTransform="capitalize">
                  {autoAdd.toString()}
                </Typography>
              </Grid>
            </Grid>
          )}

          {values.dataType === t("dataTypes.range") && (
            <Grid container item xs={12} spacing={1} alignItems="flex-end" alignContent="flex-end">
              <Grid item xs={4}>
                <InputText
                  id="lowerLimit"
                  label={t("configForm.fields.lowerLimit")}
                  placeholder={t("configForm.fields.lowerLimit")}
                  value={values.lowerLimit as unknown as string}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  handleChange={(e: any) =>
                    e.target.value
                      ? setFieldValue("lowerLimit", parseFloat(e.target.value))
                      : setFieldValue("lowerLimit", 0)
                  }
                  handleBlur={handleBlur}
                  error={touched.lowerLimit && Boolean(errors.lowerLimit) && errors.lowerLimit}
                  required
                  fullWidth
                  hideDescription
                  type="number"
                />
              </Grid>

              <Grid item xs={4}>
                <InputText
                  id="upperLimit"
                  label={t("configForm.fields.upperLimit")}
                  placeholder={t("configForm.fields.upperLimit")}
                  value={values.upperLimit as unknown as string}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  handleChange={(e: any) =>
                    e.target.value
                      ? setFieldValue("upperLimit", parseFloat(e.target.value))
                      : setFieldValue("upperLimit", values.lowerLimit + 1)
                  }
                  handleBlur={handleBlur}
                  error={touched.upperLimit && Boolean(errors.upperLimit) && touched.upperLimit}
                  required
                  fullWidth
                  hideDescription
                  type="number"
                />
              </Grid>

              <Grid item xs={4}>
                <InputText
                  id="step"
                  label={t("configForm.fields.step")}
                  placeholder={"1"}
                  value={values.step as unknown as string}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  handleChange={(e: any) => {
                    // Count of how many decimal places in the step
                    // eg: 1.15 => 2 => 2 decimal places (.15)
                    // eg: 1.156 => 3 => 3 decimal places (.156)

                    const decimalPlacesCount = e.target.value.substring(
                      e.target.value.indexOf(".") + 1,
                      e.target.value.length,
                    ).length

                    setFieldValue("decimalNumCount", decimalPlacesCount)

                    e.target.value < 0
                      ? setFieldValue("step", 1)
                      : e.target.value
                        ? setFieldValue("step", parseFloat(e.target.value))
                        : setFieldValue("step", 1)
                  }}
                  handleBlur={handleBlur}
                  error={touched.upperLimit && Boolean(errors.upperLimit) && touched.upperLimit}
                  required
                  fullWidth
                  hideDescription
                  type="number"
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      {values.dataType === t("dataTypes.range") && (
        <Grid container item xs={12} gap={2} direction="column" className={styles.containerBox}>
          <Typography variant="h3-bold">{t("configForm.titles.modelLabels")}</Typography>

          {sliders.map((item, index, arr) => (
            <ConfigurationSlider
              key={index}
              label={item.label}
              min={values.lowerLimit + values.step * index}
              max={values.upperLimit - (arr.length - (index + 1)) * values.step}
              range={[item.range]}
              onDelete={() => {
                handleRemoveLabel(index)
              }}
              isDeletable={index <= 0}
              onSliderChange={(value) => {
                const tempSliders = [...sliders]
                if (tempSliders[index]) {
                  tempSliders[index].range = value?.map((val) => +val.toFixed(values.decimalNumCount)) as [
                    number,
                    number,
                  ]
                }
                setSliders(tempSliders)
              }}
              onLabelChange={(value) => {
                const tempSliders = [...sliders]
                if (tempSliders[index]) {
                  tempSliders[index].label = value
                }
                setSliders(tempSliders)
              }}
              onChangeCommitted={(value: number[]) => {
                sliderIncrement(value, index)
                sliderDecrement(value, index)
              }}
              step={values.step ?? 1}
            />
          ))}

          <Grid item>
            <Button
              fullWidth
              onClick={() => {
                const lastSlider = sliders[sliders.length - 1]

                if (lastSlider) {
                  setSliders([
                    ...sliders,
                    {
                      label: { name: null, uuid: null },
                      range: [lastSlider.range[1] + 1, lastSlider.range[1] + 11],
                    },
                  ])
                }
              }}
            >
              <AddCircleOutline style={{ color: "var(--gray-background-1)", marginRight: "8px" }} />
              <Typography variant="h3-bold">{t("configForm.actions.addLabel")}</Typography>
            </Button>
          </Grid>

          <Grid item>
            {uncovered.length ? (
              <ConfigurationSlider
                label={{ name: t("configForm.states.uncoveredLabel"), uuid: "uuid" }}
                min={values.lowerLimit}
                max={values.upperLimit}
                range={uncovered}
                uncovered
              />
            ) : (
              <Grid container item justifyContent="center" alignItems="center" className={styles.sliderBox}>
                <Typography variant="p">{t("configForm.states.noUncovered")}</Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}
